import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Plus, Search, Tag, X } from 'lucide-react';
import { COLUMN_TO_TAG_COLOR_MAP } from '../../../constants/colorConstants';
import { useTaskColumns } from '../../../hooks/queries/useTaskColumns';
import { MobileFullScreenModal } from '../../ui/MobileFullScreenModal';
import { useViewport } from '../../../contexts/ViewportContext';

interface TagEditorProps {
  tags: string[];
  onTagsChange: (tags: string[]) => void;
  status: string;
  availableTags?: string[]; // All existing tags in the topic
  inputRef?: React.RefObject<HTMLInputElement>; // Reference to the input element for external access
  isNewForm?: boolean; // Whether this is in the new task form (for smaller font)
}

export const TagEditor: React.FC<TagEditorProps> = ({
  tags,
  onTagsChange,
  status,
  availableTags = [],
  inputRef: externalInputRef,
  isNewForm = false,
}) => {
  // Get column information for color mapping from React Query
  const { data: columns = [] } = useTaskColumns(null);
  const currentColumn = columns.find(col => col.status === status);
  const [newTag, setNewTag] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [mobileSearchTerm, setMobileSearchTerm] = useState('');
  const internalInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const mobileSearchInputRef = useRef<HTMLInputElement>(null);
  const { isMobile } = useViewport();

  // Use either the external ref or internal ref
  const inputRef = externalInputRef || internalInputRef;

  // Note: These functions have been moved inside the useEffect to avoid dependency issues

  // Update suggestions when input changes
  useEffect(() => {
    // Move these functions inside useEffect to avoid dependency issues
    const getFilteredSuggestionsLocal = (input: string) => {
      // If no input, return empty array
      if (!input.trim()) return [];

      const inputLower = input.toLowerCase();

      // Get all unique tags from available tags
      const uniqueTags = [...new Set(availableTags)];

      // Filter by:
      // 1. Tags that include the input text
      // 2. Tags that aren't already selected
      // 3. Sort alphabetically
      return uniqueTags
        .filter(tag => tag.toLowerCase().includes(inputLower) && !tags.includes(tag))
        .sort((a, b) => {
          // Prioritize tags that start with the input
          const aStartsWith = a.toLowerCase().startsWith(inputLower);
          const bStartsWith = b.toLowerCase().startsWith(inputLower);

          if (aStartsWith && !bStartsWith) return -1;
          if (!aStartsWith && bStartsWith) return 1;

          // Then sort alphabetically
          return a.localeCompare(b);
        });
    };

    const getAllAvailableTagsLocal = () => {
      // Get all unique tags from available tags
      const uniqueTags = [...new Set(availableTags)];

      // Filter out tags that are already selected
      return uniqueTags.filter(tag => !tags.includes(tag)).sort((a, b) => a.localeCompare(b));
    };

    if (newTag.trim()) {
      // If there's input, filter the suggestions
      const filtered = getFilteredSuggestionsLocal(newTag);
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
      setSelectedIndex(-1);
    } else if (showSuggestions) {
      // If no input but dropdown is shown, show all available tags
      const allTags = getAllAvailableTagsLocal();
      setSuggestions(allTags);
      setShowSuggestions(allTags.length > 0);
      setSelectedIndex(-1);
    }
  }, [newTag, tags, availableTags, showSuggestions]);

  // Close suggestions when clicking outside
  useEffect(() => {
    // Create a flag to prevent handling the same click that opens the dropdown
    let isInitialClickHandled = false;
    
    const handleClickOutside = (e: MouseEvent) => {
      // Skip the first click event after mounting to prevent immediate closing
      if (!isInitialClickHandled) {
        isInitialClickHandled = true;
        return;
      }
      
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target as Node) &&
        !inputRef.current?.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [inputRef]);

  const handleAddTag = () => {
    const trimmedTag = newTag.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      onTagsChange([...tags, trimmedTag]);
      setNewTag('');
      setShowSuggestions(false);
    }
  };

  const handleTagKeyPress = (e: React.KeyboardEvent) => {
    // If suggestions are showing, handle navigation
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, -1));
      } else if (e.key === 'Enter' || e.key === ' ' || e.key === ',') {
        // Add comma and space as tag separators
        e.preventDefault();
        if (selectedIndex >= 0) {
          // Use the selected suggestion
          if (!tags.includes(suggestions[selectedIndex])) {
            onTagsChange([...tags, suggestions[selectedIndex]]);
          }
          setNewTag('');
          setShowSuggestions(false);
        } else {
          // Use the current input
          handleAddTag();
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setShowSuggestions(false);
      }
    } else if (e.key === 'Enter' || e.key === ' ' || e.key === ',') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    onTagsChange(tags.filter(tag => tag !== tagToRemove));
  };

  const handleSelectSuggestion = (suggestion: string) => {
    if (!tags.includes(suggestion)) {
      onTagsChange([...tags, suggestion]);
    }
    setNewTag('');
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  // Helper to show all available tags
  const showAllAvailableTags = () => {
    // Get all unique tags from available tags
    const uniqueTags = [...new Set(availableTags)];

    // Filter out tags that are already selected
    const allTags = uniqueTags
      .filter(tag => !tags.includes(tag))
      .sort((a, b) => a.localeCompare(b));

    // Always show suggestions, even if we have none (we'll show a message)
    setSuggestions(allTags);

    // For mobile, open the modal instead of showing dropdown
    if (isMobile) {
      setShowMobileModal(true);
      setMobileSearchTerm('');
      // Focus the search input after modal opens
      setTimeout(() => {
        mobileSearchInputRef.current?.focus();
      }, 100);
    } else {
      // Force the dropdown to show on desktop
      setShowSuggestions(true);
    }
  };

  // Get the appropriate color class based on task status and column
  const getTagColorClass = (status: string) => {
    // Default colors for standard statuses
    switch (status) {
      case 'todo':
        return 'bg-blue-100/80 text-blue-800 border border-blue-100/80';
      case 'doing':
        return 'bg-yellow-100/80 text-yellow-800 border border-yellow-100/80';
      case 'done':
        return 'bg-green-100/80 text-green-800 border border-green-100/80';
      default:
        // For custom statuses, use column color mapping
        if (currentColumn?.color && COLUMN_TO_TAG_COLOR_MAP[currentColumn.color]) {
          return COLUMN_TO_TAG_COLOR_MAP[currentColumn.color];
        }
        return 'bg-gray-100/80 text-gray-800 border border-gray-100/80';
    }
  };

  // Filter suggestions for mobile search
  const getMobileFilteredSuggestions = () => {
    if (!mobileSearchTerm.trim()) {
      // If no search term, return all available tags that aren't already selected
      return [...new Set(availableTags)]
        .filter(tag => !tags.includes(tag))
        .sort((a, b) => a.localeCompare(b));
    }

    const searchTermLower = mobileSearchTerm.toLowerCase();
    return [...new Set(availableTags)]
      .filter(tag => tag.toLowerCase().includes(searchTermLower) && !tags.includes(tag))
      .sort((a, b) => {
        // Prioritize tags that start with the search term
        const aStartsWith = a.toLowerCase().startsWith(searchTermLower);
        const bStartsWith = b.toLowerCase().startsWith(searchTermLower);

        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;

        // Then sort alphabetically
        return a.localeCompare(b);
      });
  };

  // Add new tag directly from mobile modal
  const handleAddTagFromMobile = () => {
    const trimmedTag = mobileSearchTerm.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      onTagsChange([...tags, trimmedTag]);
      setMobileSearchTerm('');
      mobileSearchInputRef.current?.focus();
    }
  };

  return (
    <div className="flex flex-col">
      <div className="relative inline-flex flex-col">
        <div className="inline-flex items-center">
          {isMobile ? (
            // Mobile: Simple button that opens the modal
            <button
              type="button"
              onClick={() => {
                setShowMobileModal(true);
                setMobileSearchTerm('');
                // Focus search input after modal opens
                setTimeout(() => {
                  mobileSearchInputRef.current?.focus();
                }, 100);
              }}
              className="flex items-center text-blue-700"
            >
              <Tag size={isNewForm ? 14 : 16} className="mr-1.5 text-blue-700" />
              <span>Add tag</span>
            </button>
          ) : (
            // Desktop: Input with dropdown
            <>
              <button
                type="button"
                onClick={() => {
                  inputRef.current?.focus();
                  showAllAvailableTags();
                }}
                className="flex items-center text-blue-700 p-0 mr-1.5"
                title="Add tag"
              >
                <Tag size={isNewForm ? 14 : 16} className="text-blue-700" />
              </button>
              <input
                ref={inputRef}
                type="text"
                value={newTag}
                onChange={e => setNewTag(e.target.value)}
                onKeyDown={handleTagKeyPress}
                onFocus={showAllAvailableTags}
                onClick={showAllAvailableTags}
                placeholder="Add tag..."
                className={`py-1 outline-none bg-transparent text-blue-700 placeholder-blue-700`}
                style={{ width: Math.max(80, newTag.length * 8 + 30) + 'px' }}
              />
            </>
          )}
        </div>

        {/* Desktop Suggestions dropdown */}
        {!isMobile &&
          showSuggestions &&
          ReactDOM.createPortal(
            <div
              ref={suggestionsRef}
              className="fixed z-[9999] mt-1 w-56 max-h-60 overflow-auto bg-white rounded-md shadow-lg border border-gray-200"
              style={{
                top: inputRef.current ? inputRef.current.getBoundingClientRect().bottom + 5 : 0,
                left: inputRef.current ? inputRef.current.getBoundingClientRect().left : 0,
              }}
            >
              <div className="px-3 py-2 text-xs text-gray-500 font-medium bg-gray-50 border-b border-gray-200">
                {newTag.trim() ? `Tags matching "${newTag}"` : 'Available tags'}
                {suggestions.length > 0 && ` (${suggestions.length})`}
              </div>

              {suggestions.length > 0 ? (
                <ul className="py-1">
                  {suggestions.map((suggestion, index) => (
                    <li
                      key={suggestion}
                      className={`px-3 py-2 text-sm cursor-pointer flex items-center justify-between ${
                        index === selectedIndex ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => handleSelectSuggestion(suggestion)}
                      onMouseEnter={() => setSelectedIndex(index)}
                    >
                      <span>{suggestion}</span>
                      {index === selectedIndex && (
                        <span className="text-xs text-gray-500">Enter</span>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="py-3 px-3 text-sm text-gray-500 text-center">
                  {newTag.trim() ? `No tags matching "${newTag}"` : 'No available tags'}
                  <div className="mt-1 text-xs">Type to create a new tag</div>
                </div>
              )}
            </div>,
            document.body
          )}

        {/* Mobile full screen modal for tag selection */}
        {showMobileModal && isMobile && (
          <MobileFullScreenModal
            isOpen={true}
            onClose={() => setShowMobileModal(false)}
            title="Add Tags"
            footerContent={
              <button
                onClick={() => setShowMobileModal(false)}
                className="w-full py-2.5 px-4 bg-blue-500 text-white font-medium rounded-md"
              >
                Done
              </button>
            }
          >
            {/* Search input with "Add" button */}
            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <Search
                  size={18}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  ref={mobileSearchInputRef}
                  type="text"
                  value={mobileSearchTerm}
                  onChange={e => setMobileSearchTerm(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTagFromMobile();
                    }
                  }}
                  placeholder="Search or create new tag..."
                  className="w-full pl-10 pr-20 py-3 border border-gray-300 rounded-md text-base"
                />
                <button
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 px-3 py-1 bg-blue-500 text-white rounded-md flex items-center"
                  onClick={handleAddTagFromMobile}
                  disabled={!mobileSearchTerm.trim()}
                >
                  <Plus size={16} className="mr-1" />
                  <span>Add</span>
                </button>
              </div>

              {/* Selected tags section */}
              {tags.length > 0 && (
                <div className="mt-4">
                  <div className="text-sm font-medium text-gray-500 mb-2">Selected tags:</div>
                  <div className="flex flex-wrap gap-2">
                    {tags.map(tag => (
                      <div
                        key={tag}
                        className={`${getTagColorClass(status)} text-sm flex items-center gap-1 rounded-full px-3 py-1.5`}
                      >
                        <span>{tag}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="hover:text-gray-700"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Available tags list */}
            <div className="divide-y divide-gray-100">
              {/* Header for available tags */}
              <div className="px-4 py-3 bg-gray-50">
                <h3 className="font-medium text-gray-700">
                  {mobileSearchTerm ? `Tags matching "${mobileSearchTerm}"` : 'Available tags'}
                </h3>
              </div>

              {/* List of available tags */}
              <div className="max-h-[60vh] overflow-y-auto">
                {getMobileFilteredSuggestions().length > 0 ? (
                  getMobileFilteredSuggestions().map(tag => (
                    <button
                      key={tag}
                      className="w-full text-left px-4 py-4 flex items-center justify-between border-b border-gray-100"
                      onClick={() => {
                        handleSelectSuggestion(tag);
                        // Keep the modal open after selection
                      }}
                    >
                      <span className="text-base">{tag}</span>
                      <Plus size={18} className="text-blue-500" />
                    </button>
                  ))
                ) : (
                  <div className="p-4 text-center text-gray-500">
                    {mobileSearchTerm.trim()
                      ? `No tags matching "${mobileSearchTerm}"`
                      : 'No available tags'}
                    <div className="mt-1 text-sm">
                      {mobileSearchTerm.trim() &&
                        'Type in the search box and press Add to create a new tag'}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </MobileFullScreenModal>
        )}
      </div>

      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-5">
          {tags.map(tag => (
            <div
              key={tag}
              className={`${getTagColorClass(status)} ${isNewForm ? 'text-sm' : 'text-base'} flex items-center gap-1 rounded-full px-3 py-1`}
            >
              <span>{tag}</span>
              <button
                type="button"
                onClick={() => handleRemoveTag(tag)}
                className="hover:text-gray-700"
              >
                <X size={isNewForm ? 12 : 14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
